import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { LOGO_URL, NAV_LINKS } from "../../lib/projects";

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollToAnchor = (hash) => {
        const el = document.querySelector(hash);
        if (!el) return;
        if (window.__lenis) {
            window.__lenis.scrollTo(el, { offset: -20, duration: 1.4 });
        } else {
            el.scrollIntoView({ behavior: "smooth" });
        }
    };

    // Hybrid handler:
    //   • route links → router navigation + reset scroll
    //   • anchor links (#work) →
    //       on /  → smooth-scroll to the section
    //       elsewhere → navigate to /<hash> and let Home pick up location.hash
    const handleNav = (link) => {
        setOpen(false);

        if (link.type === "route") {
            if (location.pathname !== link.href) {
                navigate(link.href);
            }
            return;
        }

        if (location.pathname !== "/") {
            navigate("/" + link.href);
            return;
        }

        scrollToAnchor(link.href);
    };

    const handleLogo = () => {
        setOpen(false);
        if (location.pathname !== "/") {
            navigate("/");
            return;
        }
        if (window.__lenis) {
            window.__lenis.scrollTo(0, { duration: 1.2 });
        } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    const isActive = (link) => {
        if (link.type === "route") return location.pathname === link.href;
        return false;
    };

    return (
        <>
            <motion.header
                data-testid="site-navbar"
                initial={{ y: -40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="absolute left-0 right-0 top-0 z-[60] px-8 py-10 md:px-14 md:py-14"
                style={{ mixBlendMode: "difference" }}
            >
                <div className="mx-auto flex w-full max-w-[1800px] items-center justify-between">
                    {/* Logo */}
                    <button
                        data-testid="nav-logo"
                        onClick={handleLogo}
                        className="flex items-center gap-3"
                        aria-label="Mad House Studios home"
                    >
                        <img
                            src={LOGO_URL}
                            alt="Mad House Studios"
                            className="h-14 w-auto md:h-20"
                            draggable={false}
                        />
                    </button>

                    {/* Desktop links — right aligned */}
                    <nav className="hidden items-center gap-10 md:flex">
                        {NAV_LINKS.map((l, i) => (
                            <button
                                key={`${l.label}-${i}`}
                                data-testid={`nav-link-${l.label.toLowerCase()}`}
                                onClick={() => handleNav(l)}
                                className={`link-underline font-mono text-[11px] uppercase tracking-[0.3em] ${
                                    isActive(l) ? "text-[#DB162F]" : "text-fog-primary"
                                }`}
                                data-cursor="hover"
                            >
                                {l.label}
                            </button>
                        ))}
                    </nav>

                    {/* Mobile toggle */}
                    <button
                        data-testid="nav-mobile-toggle"
                        className="md:hidden"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </motion.header>

            {/* Mobile menu */}
            <AnimatePresence>
                {open ? (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-x-0 top-[68px] z-[55] border-y border-ink-border bg-ink px-6 py-8 md:hidden"
                    >
                        <nav className="flex flex-col gap-5">
                            {NAV_LINKS.map((l, i) => (
                                <button
                                    key={`${l.label}-m-${i}`}
                                    onClick={() => handleNav(l)}
                                    className={`text-left font-display text-3xl uppercase tracking-tight ${
                                        isActive(l) ? "text-[#DB162F]" : "text-fog-primary"
                                    }`}
                                >
                                    {l.label}
                                </button>
                            ))}
                        </nav>
                    </motion.div>
                ) : null}
            </AnimatePresence>
        </>
    );
};

export default Navbar;
